// Video ingest pipeline (§10.2, serial rewrite). Two stages:
//   Stage S — parse episodes one at a time, IN ORDER. Each episode's Gemini call
//     watches the video WITH the running cross-episode context (registry + plot
//     digest + open threads) and returns both the episode content and a registry
//     delta. Identity is resolved by plot as we go, so there is no parallel
//     transcribe + roster-merge + correct + speaker compensation. Checkpoint =
//     per episode: ep/ep_NNN.json (content) + context.json (running context,
//     authoritative for resume). script.json is rebuilt after each episode so an
//     editor can stream it as it grows.
//   Stage F — the deterministic tail, SHARED with text ingest: resolve structured
//     asset names to their revealed identities → docsToNameScript → assignProvisionalIds
//     → stampRegistryMetadata → governDeterministic → renumberIds → validateScriptV3.
// The final script.json is the same v4 dialect as text ingest.

import * as fs from "node:fs";
import * as path from "node:path";

import { CliError, EXIT_INPUT } from "../../common.js";
import { formatFailure } from "../../failures.js";
import { assignProvisionalIds } from "../../domain/ingest/assembler.js";
import { stateIdentityName } from "../../domain/ingest/state-identity.js";
import { governDeterministic } from "../../domain/ingest/govern.js";
import { validateProductionStandard } from "../../domain/ingest/production-validation.js";
import { renumberIds } from "../../domain/ingest/renumber.js";
import { validateScriptV3 } from "../../domain/ingest/validation.js";
import {
  classifyVideoSourceEvolution,
  validateContinuousEpisodeSequence,
  type VideoSourceEpisodeIdentity,
} from "../../domain/ingest/video-source-evolution.js";
import { requireScriptV3 } from "../../domain/script/v3/parse.js";
import {
  emptyV4IdentityMap,
  newScriptV4FromV3WithIdentity,
  type V3IdentityKeys,
  type V4IdentityMap,
} from "../../domain/script/v4/from-v3.js";
import type { ScriptV4 } from "../../domain/script/v4/types.js";
import { validateScriptV4 } from "../../domain/script/v4/validation.js";
import { applyRename, docsToNameScript } from "../../domain/ingest/video-apply.js";
import { buildVideoStateEvidencePlan } from "../../domain/ingest/video-state-evidence.js";
import {
  applyDelta,
  buildAliasMap,
  deltaToDoc,
  emptyContext,
  renderContext,
  stampRegistryMetadata,
  type RunContext,
} from "../../domain/ingest/video-context.js";
import type { VideoEpisodeDoc } from "../../domain/ingest/video-records.js";
import { resolveSource } from "../../infra/ingest/source-resolver.js";
import { discoverEpisodes } from "../../infra/ingest/video-discovery.js";
import {
  snapshotVideoSource,
  videoSourceFingerprint,
} from "../../infra/ingest/video-source-snapshot.js";
import { transcodeForUpload } from "../../infra/ingest/video-transcode.js";
import { materializeVideoStateEvidence } from "../../infra/ingest/video-state-evidence.js";
import { WorkspaceStore } from "../../infra/ingest/workspace-store.js";
import { withIngestWorkspaceLock } from "../../infra/ingest/workspace-lock.js";
import { runVideoSerial, type VideoSerialResult } from "../../llm/tasks/ingest/video-serial.js";
import {
  MAX_INLINE_VIDEO_BYTES,
  type LlmTaskExecutor,
  type TaskVideoPort,
  type VideoUploadMode,
} from "../../llm/types.js";
import { UsageLedger } from "./usage-report.js";
import { createEpisodeErrorSidecar, recoveryHint, type IngestErrorSidecar } from "./failure.js";
import {
  assertVideoSkipSources,
  markSkippedEpisodes,
  readAcceptedVideoDocs,
  reconcileVideoSkips,
  type VideoSkipRecord,
} from "./video-skip-state.js";

export const VIDEO_PIPELINE_VERSION = "19"; // detailed reproducible visual-state descriptions
const COMPATIBLE_VIDEO_PIPELINE_VERSIONS = new Set([VIDEO_PIPELINE_VERSION]);
const pad = (n: number): string => String(n).padStart(3, "0");
const VIDEO_VALIDATION_ATTEMPTS = 2;

export interface VideoIngestOptions {
  sourcePath: string;
  workspacePath: string;
  executor: LlmTaskExecutor;
  fps?: number;
  force?: boolean;
  step?: boolean;
  uploadMode?: VideoUploadMode;
  signal?: AbortSignal;
}

export interface VideoIngestReport {
  workspacePath: string;
  episodes: number;
  processed: number;
  reused: number;
  skipped: number;
  skippedEpisodes: number[];
  completed: number;
  remaining: number;
  complete: boolean;
  nextEpisode: number | null;
  uploadMode: VideoUploadMode;
  assets: { actors: number; locations: number; props: number };
  evidence: { states: number; samples: number; ready: number };
  scriptPath: string;
  validation: { ok: boolean; errors: number; warnings: number };
}

export type VideoSourceKind = "video-file" | "video-directory";

export interface VideoIngestManifest {
  version: 2;
  pipeline_version: string;
  updated: string;
  source: {
    kind: VideoSourceKind;
    path: string;
    sha256: string;
  };
  episodes: VideoSourceEpisodeIdentity[];
  fps: number;
  current_error?: IngestErrorSidecar;
  skips?: VideoSkipRecord[];
}

// Per-episode timing, appended a line at a time to timing.jsonl so a long run's
// costs are inspectable live (which episode uploaded/generated how long).
interface EpisodeTiming {
  episode: number;
  uploadMode: VideoUploadMode;
  status: "succeeded" | "failed";
  totalMs: number;
  transcodeMs?: number;
  uploadMs?: number;
  generateMs?: number;
  uploadBytes?: number;
  transcoded?: boolean;
  note?: string;
}

function fileSize(p: string): number {
  try { return fs.statSync(p).size; } catch { return 0; }
}

export function assertInlineVideoSize(filePath: string, episode: number): void {
  const bytes = fileSize(filePath);
  if (bytes < MAX_INLINE_VIDEO_BYTES) return;
  throw new CliError(`Episode ${episode} is too large for inline base64 video input.`, {
    exitCode: EXIT_INPUT,
    errorCode: "INGEST_VIDEO_INLINE_TOO_LARGE",
    hint: "Use --upload-mode file-api, or provide a source that transcodes below the Gemini inline limit.",
    data: {
      expected: [`video smaller than ${MAX_INLINE_VIDEO_BYTES} bytes after ingest transcoding`],
      actual: [`${bytes} bytes`],
    },
  });
}

function appendTiming(store: WorkspaceStore, t: EpisodeTiming): void {
  try {
    const mb = t.uploadBytes ? (t.uploadBytes / 1024 / 1024).toFixed(1) : "?";
    const line = JSON.stringify({ ...t, uploadMB: mb, at: new Date().toISOString() }) + "\n";
    fs.appendFileSync(store.path("timing.jsonl"), line);
  } catch { /* timing is diagnostic-only, never fail the run */ }
}

function sourceMismatch(reason: string): CliError {
  return new CliError("This workspace's accepted video sequence no longer matches the source.", {
    exitCode: EXIT_INPUT,
    errorCode: "INGEST_SOURCE_MISMATCH",
    hint: "Restore the accepted episode prefix, or choose a fresh --workspace-path for a different source.",
    data: { actual: [reason] },
  });
}

export function readCurrentVideoManifest(store: WorkspaceStore): VideoIngestManifest | null {
  if (!store.has("manifest.json")) return null;
  const header = store.readJson<{ pipeline_version?: string }>("manifest.json");
  if (!header.pipeline_version || !COMPATIBLE_VIDEO_PIPELINE_VERSIONS.has(header.pipeline_version)) {
    throw new CliError("This workspace uses an incompatible video-ingest pipeline.", {
      exitCode: EXIT_INPUT,
      errorCode: "INGEST_PIPELINE_MISMATCH",
      hint: "Choose a fresh --workspace-path and run ingest again.",
      data: { actual: [`workspace pipeline ${header.pipeline_version ?? "(missing)"}; current ${VIDEO_PIPELINE_VERSION}`] },
    });
  }
  return store.readJson<VideoIngestManifest>("manifest.json");
}

export function migrateVideoManifest(
  store: WorkspaceStore,
  manifest: VideoIngestManifest,
): VideoIngestManifest {
  if (manifest.pipeline_version === VIDEO_PIPELINE_VERSION) return manifest;
  const migrated: VideoIngestManifest = {
    ...manifest,
    pipeline_version: VIDEO_PIPELINE_VERSION,
    updated: new Date().toISOString(),
  };
  store.writeJson("manifest.json", migrated);
  return migrated;
}

function checkSourceRoot(
  previous: VideoIngestManifest | null,
  sourceKind: VideoSourceKind,
  _sourcePath: string,
): void {
  if (!previous) return;
  if (previous.source.kind !== sourceKind) {
    throw sourceMismatch(`source kind changed from ${previous.source.kind} to ${sourceKind}`);
  }
  // A directory relocation is not a source identity change. The accepted
  // sequence is guarded below by episode number, media type and file sha256;
  // permitting a new root lets projects safely relink moved/NAS-hosted media.
}

function checkLifecycle(
  store: WorkspaceStore,
  previous: VideoIngestManifest | null,
  episodes: VideoSourceEpisodeIdentity[],
  force: boolean,
): void {
  if (previous) {
    const evolution = classifyVideoSourceEvolution(previous.episodes, episodes);
    if (evolution.kind === "incompatible") throw sourceMismatch(evolution.reason);
  }

  if (force) {
    fs.rmSync(store.path("ep"), { recursive: true, force: true });
    fs.rmSync(store.path("cache"), { recursive: true, force: true });
    fs.rmSync(store.path("evidence"), { recursive: true, force: true });
    for (const f of [
      "context.json",
      "script.initial.json",
      "script.json",
      "validation.json",
      "asset-audit.json",
      "id_map.json",
      "timing.jsonl",
      "usage.json",
      "v4_identity_map.json",
      "skipped.json",
    ]) fs.rmSync(store.path(f), { force: true });
  }
}

// Resolve structured asset names through the historical-name alias map before
// convergence. All source prose stays verbatim: aliases identify assets, not
// occurrences in free text (e.g. actor alias「头」also occurs in「镜头」).
// Deep-cloned so the persisted ep docs are never mutated.
function renameDocs(docs: VideoEpisodeDoc[], alias: Map<string, string>): VideoEpisodeDoc[] {
  return docs.map((d) => {
    const c = structuredClone(d);
    for (const a of c.actors) a.name = applyRename(a.name, alias) ?? a.name;
    for (const sc of c.scenes) {
      for (const ch of sc.characters) ch.name = applyRename(ch.name, alias) ?? ch.name;
      if (sc.location) sc.location = applyRename(sc.location, alias) ?? sc.location;
      // Rename props through the alias map, then collapse any pairs that now
      // share a canonical name (surface A + surface B → same entity).
      const renamedProps: string[] = [];
      const seenProps = new Set<string>();
      for (const prop of sc.props) {
        const name = (applyRename(prop, alias) ?? prop).trim();
        if (!name) continue;
        const key = name.normalize("NFC").replace(/\s+/g, " ").trim().toLowerCase();
        if (seenProps.has(key)) continue;
        seenProps.add(key);
        renamedProps.push(name);
      }
      sc.props = renamedProps;
      for (const an of sc.anchors) {
        an.speaker = applyRename(an.speaker, alias);
      }
    }
    return c;
  });
}

interface FinalizeResult {
  initial: Record<string, unknown>;
  final: Record<string, unknown>;
  idMap: unknown;
  validation: ReturnType<typeof validateScriptV4>;
  identityMap: V4IdentityMap;
}

function validationRetryContext(
  contextText: string,
  errors: FinalizeResult["validation"]["errors"],
): string {
  const details = errors.slice(0, 8).map((error) => `- ${error.path}: ${error.message}`).join("\n");
  return `${contextText}\n\n========== 上一次输出的整合校验反馈 ==========
上一次对本集的结构化输出没有写入 checkpoint，因为与既有剧本合并后违反了以下约束：
${details}
请重新观看本集并返回完整 JSON。保持原片事实、对白、镜头与已知人物不变，只修正导致校验失败的人物命名、资产绑定或 registry_delta。人物规范名不得用括号备注区分角色；角色职能写入 role，不同人物使用归一后仍唯一的规范名。`;
}

const identityNorm = (value: string): string =>
  value.normalize("NFC").replace(/\s+/g, " ").trim().toLowerCase();

function videoIdentityKeys(
  assets: ReturnType<typeof assignProvisionalIds>["assets"],
  registry: RunContext["registry"],
): V3IdentityKeys {
  const aliases = buildAliasMap(registry);
  const resolvedName = (name: string): string => aliases.get(name) ?? name;
  const keyByCanonical = new Map<string, string>();
  for (const entity of registry) {
    const canonical = resolvedName(entity.name);
    const exactTarget = registry.find((candidate) =>
      candidate.kind === entity.kind
      && identityNorm(candidate.name) === identityNorm(canonical));
    const source = exactTarget ?? entity;
    const canonicalKey = `${entity.kind}:${identityNorm(canonical)}`;
    if (!keyByCanonical.has(canonicalKey)) {
      keyByCanonical.set(
        canonicalKey,
        `video:${source.kind}:${source.firstEpisode}:${identityNorm(source.identitySeed)}`,
      );
    }
  }
  const actor: Record<string, string> = {};
  const location: Record<string, string> = {};
  const prop: Record<string, string> = {};
  const state: Record<string, Record<string, string>> = {};
  const byKind = { actor, location, prop };
  for (const asset of assets) {
    const name = asset.names[0] ?? asset.provisional_id;
    const key = keyByCanonical.get(`${asset.kind}:${identityNorm(name)}`)
      ?? `video:${asset.kind}:source:${identityNorm(name)}`;
    byKind[asset.kind][asset.provisional_id] = key;
    state[asset.provisional_id] = Object.fromEntries(
      asset.states.map((state) => [
        state.id,
        `${key}:state:${stateIdentityName(state.name)}`,
      ]),
    );
  }
  return { actor, location, prop, state };
}

// Deterministic tail shared with text ingest. Pure (no LLM); ms-fast, so it runs
// after every episode (streaming) and once at the end (gated on validation).
export function finalizeVideoScript(
  docs: VideoEpisodeDoc[],
  ctx: RunContext,
  title: string,
  priorIdentity: V4IdentityMap,
  skips: readonly VideoSkipRecord[] = [],
): FinalizeResult {
  const alias = buildAliasMap(ctx.registry);
  const renamed = renameDocs(docs, alias);
  const nameScript = docsToNameScript(renamed, title);
  const { script, assets, identityKeys } = assignProvisionalIds(nameScript);
  stampRegistryMetadata(script, ctx.registry);
  const governed = governDeterministic(script);
  const { script: final, idMap } = renumberIds(governed);
  const intermediateValidation = validateScriptV3(final);
  if (!intermediateValidation.ok) {
    return { initial: script, final, idMap, validation: intermediateValidation, identityMap: priorIdentity };
  }
  // Persist identities against the pre-renumber ingest keys. Contiguous V3 ids
  // shift when an earlier entity disappears and therefore are not identities.
  const converted = newScriptV4FromV3WithIdentity(
    requireScriptV3(governed),
    priorIdentity,
    {
      ...videoIdentityKeys(assets, ctx.registry),
      ...identityKeys,
    },
  );
  const scriptV4 = converted.script;
  markSkippedEpisodes(scriptV4, skips);
  const structuralValidation = validateScriptV4(scriptV4);
  const descriptionWarnings = [
    ...scriptV4.actors.map((asset, index) => ({ asset, path: `$.actors[${index}]` })),
    ...scriptV4.locations.map((asset, index) => ({ asset, path: `$.locations[${index}]` })),
    ...scriptV4.props.map((asset, index) => ({ asset, path: `$.props[${index}]` })),
  ].flatMap(({ asset, path }) => [
    ...(!asset.description.trim() ? [{ code: "video_asset_description_missing", path: `${path}.description`, message: "video asset needs a source-grounded description" }] : []),
    ...asset.states.flatMap((state, stateIndex) => !state.description.trim()
      ? [{ code: "video_state_description_missing", path: `${path}.states[${stateIndex}].description`, message: "video asset state needs a source-grounded visual description" }]
      : []),
  ]);
  const validation = {
    ...structuralValidation,
    warnings: [
      ...structuralValidation.warnings,
      ...validateProductionStandard(scriptV4),
      ...descriptionWarnings,
      ...skips.map((record) => ({
        code: "episode_skipped",
        path: `$.episodes[${record.episode - 1}]`,
        message: `Episode ${record.episode} was manually skipped: ${record.reason}`,
      })),
    ],
  };
  return { initial: script, final: scriptV4, idMap, validation, identityMap: converted.identityMap };
}

export function publishFinalizedVideoWorkspace(
  store: WorkspaceStore,
  episodes: ReadonlyArray<{ episode: number }>,
  ctx: RunContext,
  title: string,
  identityMap: V4IdentityMap,
  skips: readonly VideoSkipRecord[],
): FinalizeResult {
  const finalized = finalizeVideoScript(
    readAcceptedVideoDocs(store, episodes, ctx.lastEpisode, skips),
    ctx,
    title,
    identityMap,
    skips.filter((record) => record.episode <= ctx.lastEpisode),
  );
  const { initial, final, idMap, validation } = finalized;
  store.writeJson("script.initial.json", initial);
  store.writeJson("id_map.json", idMap);
  store.writeJson("validation.json", validation);
  store.writeJson("v4_identity_map.json", finalized.identityMap);
  if (!validation.ok) {
    throw new CliError("Final script.json failed validation.", {
      exitCode: EXIT_INPUT,
      errorCode: "INGEST_VALIDATION_FAILED",
      hint: "Inspect validation.json.",
      data: { actual: validation.errors.slice(0, 5).map((error) => `${error.path}: ${error.message}`) },
    });
  }
  store.writeJson("script.json", final);
  return finalized;
}

async function materializeStateEvidenceForPrefix(
  store: WorkspaceStore,
  docs: VideoEpisodeDoc[],
  script: ScriptV4,
  context: RunContext,
  sources: readonly VideoSourceEpisodeIdentity[],
  sourceSha256: string,
  signal?: AbortSignal,
) {
  return materializeVideoStateEvidence(
    store,
    buildVideoStateEvidencePlan(docs, script, context, sources, sourceSha256),
    signal,
  );
}

async function runVideoIngestUnlocked(opts: VideoIngestOptions): Promise<VideoIngestReport> {
  const resolved = resolveSource(opts.sourcePath);
  if (resolved.kind === "text") throw new CliError("runVideoIngest received a text source.", {
                                        exitCode: EXIT_INPUT,
                                        errorCode: "INGEST_WRONG_PIPELINE",
                                      });
  const episodes = [...discoverEpisodes(resolved)].sort((a, b) => a.episode - b.episode);
  const sequenceError = validateContinuousEpisodeSequence(episodes);
  if (sequenceError) {
    throw new CliError("Video episodes must form one continuous sequence starting at episode 1.", {
      exitCode: EXIT_INPUT,
      errorCode: "INGEST_VIDEO_SEQUENCE",
      hint: "Add or rename files so the discovered episode numbers are 1..N with no gaps.",
      data: { actual: [sequenceError] },
    });
  }
  const fps = opts.fps ?? 3;
  const force = opts.force ?? false;
  const step = opts.step ?? false;
  const uploadMode = opts.uploadMode ?? "file-api";
  const sig = opts.signal;

  const store = new WorkspaceStore(opts.workspacePath);
  store.ensure();
  const previousManifest = readCurrentVideoManifest(store);
  const sourcePath = path.resolve(resolved.path);
  checkSourceRoot(previousManifest, resolved.kind, sourcePath);
  const sourceEpisodes = snapshotVideoSource(
    episodes,
    previousManifest?.episodes ?? [],
    previousManifest?.updated,
  );
  checkLifecycle(
    store,
    previousManifest,
    sourceEpisodes,
    force,
  );
  const manifest: VideoIngestManifest = {
    version: 2,
    pipeline_version: VIDEO_PIPELINE_VERSION,
    updated: new Date().toISOString(),
    source: {
      kind: resolved.kind,
      path: sourcePath,
      sha256: videoSourceFingerprint(sourceEpisodes),
    },
    episodes: sourceEpisodes,
    fps,
    skips: force ? [] : previousManifest?.skips ?? [],
  };
  store.writeJson("manifest.json", manifest);

  const usage = new UsageLedger(store, opts.executor);
  let runStatus: "succeeded" | "failed" = "failed";
  try {
  let port: TaskVideoPort | null = null;
  const title = deriveVideoTitle(opts.sourcePath);
  let ctx: RunContext = !force && store.has("context.json") ? store.readJson<RunContext>("context.json") : emptyContext();
  const skipLedger = { schema_version: 1 as const, episodes: manifest.skips ?? [] };
  assertVideoSkipSources(skipLedger.episodes, sourceEpisodes);
  if (!force) reconcileVideoSkips(store, ctx, skipLedger.episodes);
  const skippedEpisodes = new Set(skipLedger.episodes.map((record) => record.episode));
  let identityMap = !force && store.has("v4_identity_map.json")
    ? store.readJson<V4IdentityMap>("v4_identity_map.json")
    : emptyV4IdentityMap();
  let reused = 0;
  let processed = 0;

  // ===== Stage S: serial per-episode parse =====
  for (const e of episodes) {
    const rel = `ep/ep_${pad(e.episode)}.json`;
    if (!force && skippedEpisodes.has(e.episode) && e.episode <= ctx.lastEpisode) continue;
    // Resume: skip only when the episode's content exists AND the running context
    // already accounts for it (a crash between the two writes → rerun this ep).
    if (!force && store.has(rel) && e.episode <= ctx.lastEpisode) {
      reused++;
      opts.executor.recordReuse("ingest.video-serial");
      const at = Date.now();
      usage.flush({
        scope: { kind: "episode", episode: e.episode },
        status: "reused",
        startedAtMs: at,
        endedAtMs: at,
      });
      continue;
    }
    if (step && processed >= 1) break;

    // Persist the exact context fed to Gemini for this episode (inspectable: what
    // prior-episode knowledge the model saw), before the call so a failed episode
    // still shows its input.
    const ctxMd = renderContext(ctx);
    store.writeText(`ep/ep_${pad(e.episode)}.context.md`, ctxMd);
    let md!: string;
    let doc!: VideoEpisodeDoc;
    let nextContext!: RunContext;
    let streamed!: FinalizeResult;
    const episodeStartedMs = Date.now();
    const timing: EpisodeTiming = { episode: e.episode, uploadMode, status: "succeeded", totalMs: 0 };
    let failurePhase = "transcode";
    try {
      // Shrink the source before upload while retaining its frame rate so the
      // requested provider sampling rate still has the original temporal detail.
      // Best-effort: falls back to the original.
      const tStart = Date.now();
      const enc = await transcodeForUpload(e.path, store.path(`cache/ep_${pad(e.episode)}.mp4`), e.mime, sig);
      timing.transcodeMs = Date.now() - tStart;
      timing.uploadBytes = fileSize(enc.path);
      timing.transcoded = enc.transcoded;
      if (enc.note) timing.note = enc.note;
      if (uploadMode === "base64") assertInlineVideoSize(enc.path, e.episode);

      failurePhase = "upload";
      port ??= opts.executor.videoPort("ingest.video-serial");
      const uStart = Date.now();
      const media = await port.prepare(enc.path, enc.mime, uploadMode, sig);
      timing.uploadMs = Date.now() - uStart;
      try {
        failurePhase = "generate";
        const gStart = Date.now();
        const acceptedDocs = readAcceptedVideoDocs(
          store,
          episodes,
          ctx.lastEpisode,
          skipLedger.episodes,
        );
        let generationContext = ctxMd;
        for (let attempt = 1; attempt <= VIDEO_VALIDATION_ATTEMPTS; attempt++) {
          let generated: VideoSerialResult;
          try {
            generated = await runVideoSerial(
              port, media, e.episode, generationContext, fps, sig,
              { path: enc.path, mime: enc.mime },
              (event) => fs.appendFileSync(store.path("ingest-validation.jsonl"), JSON.stringify(event) + "\n"),
            );
          } catch (error) {
            if (!(error instanceof CliError) || error.errorCode !== "INGEST_VIDEO_SERIAL_FAILED"
              || attempt === VIDEO_VALIDATION_ATTEMPTS) throw error;
            generationContext = `${ctxMd}\n\n上一次输出未被接受，也未推进 checkpoint：${error.message}。请重新观看当前集，只按视频事实返回符合 schema 的完整 JSON，scenes 至少包含一个实际场景；不要只写梗概或设定增量。`;
            continue;
          }
          const candidateDoc = deltaToDoc(generated.output, e.episode);
          const candidateContext = structuredClone(ctx);
          applyDelta(candidateContext, generated.output, e.episode);
          const candidate = finalizeVideoScript(
            [...acceptedDocs, candidateDoc],
            candidateContext,
            title,
            identityMap,
            skipLedger.episodes.filter((record) => record.episode <= e.episode),
          );
          if (candidate.validation.ok) {
            md = generated.md;
            doc = candidateDoc;
            nextContext = candidateContext;
            streamed = candidate;
            break;
          }
          failurePhase = "validation";
          if (attempt === VIDEO_VALIDATION_ATTEMPTS) {
            throw new CliError("Final script.json failed validation after model correction.", {
              exitCode: EXIT_INPUT,
              errorCode: "INGEST_VALIDATION_FAILED",
              hint: "Rerun ingest to continue with the next configured model route.",
              data: {
                actual: candidate.validation.errors.slice(0, 8)
                  .map((error) => `${error.path}: ${error.message}`),
              },
            });
          }
          generationContext = validationRetryContext(ctxMd, candidate.validation.errors);
        }
        timing.generateMs = Date.now() - gStart;
      } finally {
        await port.release(media, sig).catch(() => {}); // best-effort cleanup; serial never reuses media
        // Drop the local transcode once uploaded+parsed — it is a disposable cache.
        if (enc.transcoded) fs.rmSync(enc.path, { force: true });
      }
    } catch (err) {
      const endedAtMs = Date.now();
      timing.status = "failed";
      timing.totalMs = endedAtMs - episodeStartedMs;
      appendTiming(store, timing);
      usage.flush({
        scope: { kind: "episode", episode: e.episode },
        status: "failed",
        startedAtMs: episodeStartedMs,
        endedAtMs,
        stages: timing,
      });
      const sidecarPhase = err instanceof CliError && err.errorCode === "INGEST_VIDEO_SERIAL_FAILED"
        ? "parse"
        : failurePhase;
      const sidecar = createEpisodeErrorSidecar(err, e.episode, sidecarPhase);
      const detail = formatFailure(sidecar.failure);
      store.writeJson(`ep/ep_${pad(e.episode)}.error.json`, sidecar);
      store.writeJson("manifest.json", { ...manifest, updated: sidecar.occurred_at, current_error: sidecar });
      if (err instanceof CliError && (
        err.errorCode === "INGEST_VIDEO_INLINE_TOO_LARGE"
        || err.errorCode === "INGEST_VALIDATION_FAILED"
        || err.errorCode === "INGEST_VIDEO_TIMESTAMP_MISSING"
      )) throw err;
      throw new CliError(`Episode ${e.episode} failed serial parse; later episodes depend on its context.`, {
              exitCode: EXIT_INPUT,
              errorCode: "INGEST_SERIAL_INCOMPLETE",
              hint: recoveryHint(sidecar.failure),
              data: { actual: [detail] },
            });
    }

    ctx = nextContext;
    processed += 1;
    store.writeText(`ep/ep_${pad(e.episode)}.md`, md); // raw transcript for human review
    store.writeJson(rel, doc);
    store.writeJson("context.json", ctx);
    store.clearError(`ep/ep_${pad(e.episode)}.error.json`);
    const episodeEndedMs = Date.now();
    timing.totalMs = episodeEndedMs - episodeStartedMs;
    appendTiming(store, timing); // per-episode transcode/upload/generate durations
    usage.flush({
      scope: { kind: "episode", episode: e.episode },
      status: "succeeded",
      startedAtMs: episodeStartedMs,
      endedAtMs: episodeEndedMs,
      stages: timing,
    });

    // Publish only after the candidate episode and its updated registry validate
    // together. A bad model response is never allowed to advance the checkpoint.
    identityMap = streamed.identityMap;
    store.writeJson("v4_identity_map.json", identityMap);
    store.writeJson("script.json", streamed.final);
  }

  // ===== Stage F: deterministic finalize (gated) =====
  const completed = Math.min(ctx.lastEpisode, episodes.length);
  const remaining = episodes.length - completed;
  const complete = remaining === 0;
  const nextEpisode = complete ? null : episodes[completed]?.episode ?? null;
  const finalized = publishFinalizedVideoWorkspace(
    store,
    episodes.slice(0, completed),
    ctx,
    title,
    identityMap,
    skipLedger.episodes,
  );
  const { final, validation } = finalized;
  const evidence = complete
    ? await materializeStateEvidenceForPrefix(
      store,
      readAcceptedVideoDocs(store, episodes, ctx.lastEpisode, skipLedger.episodes),
      final as ScriptV4,
      ctx,
      sourceEpisodes,
      manifest.source.sha256,
      sig,
    )
    : { items: [] };
  const evidenceSamples = evidence.items.flatMap((item) => item.samples);

  const g = final as { actors?: unknown[]; locations?: unknown[]; props?: unknown[] };
  runStatus = "succeeded";
  return {
    workspacePath: store.root,
    episodes: episodes.length,
    processed,
    reused,
    skipped: skipLedger.episodes.length,
    skippedEpisodes: skipLedger.episodes.map((record) => record.episode),
    completed,
    remaining,
    complete,
    nextEpisode,
    uploadMode,
    assets: { actors: g.actors?.length ?? 0, locations: g.locations?.length ?? 0, props: g.props?.length ?? 0 },
    evidence: {
      states: evidence.items.length,
      samples: evidenceSamples.length,
      ready: evidenceSamples.filter((sample) => sample.frame_status === "ready").length,
    },
    scriptPath: store.path("script.json"),
    validation: { ok: validation.ok, errors: validation.errors.length, warnings: validation.warnings.length },
  };
  } finally {
    usage.finish(runStatus);
  }
}

export async function runVideoIngest(opts: VideoIngestOptions): Promise<VideoIngestReport> {
  return withIngestWorkspaceLock(opts.workspacePath, () => runVideoIngestUnlocked(opts));
}

export function deriveVideoTitle(sourcePath: string): string {
  const base = path.basename(sourcePath, path.extname(sourcePath));
  return base || "";
}
